import { useEffect } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';

import { cn } from '@broccoli/ui';

import { useApprovals, useLogout, useMe } from '@/shared/api/queries';
import { offlineQueue } from '@/shared/lib/offline-queue';
import { OfflineBanner } from '@/shared/ui/offline-banner';
import { Avatar, Skeleton } from '@/shared/ui/primitives';

import type { ReactNode } from 'react';

const NAV: { group: string; items: { to: string; label: string; badge?: 'approvals' }[] }[] = [
  {
    group: 'งาน',
    items: [
      { to: '/', label: 'วันนี้' },
      { to: '/pipeline', label: 'ไปป์ไลน์' },
      { to: '/jobs', label: 'งานทั้งหมด' },
      { to: '/approvals', label: 'รออนุมัติ', badge: 'approvals' },
    ],
  },
  {
    group: 'หน้างาน',
    items: [
      { to: '/site-report', label: 'รายงานประจำวัน' },
      { to: '/procurement', label: 'จัดซื้อ' },
    ],
  },
  {
    group: 'รายงาน',
    items: [
      { to: '/reports', label: 'ตารางไขว้' },
      { to: '/reports/workload', label: 'งานในมือ' },
    ],
  },
  {
    group: 'ตั้งค่าระบบ',
    items: [
      { to: '/admin/stages', label: 'สเตป · SLA' },
      { to: '/admin/roles', label: 'สิทธิ์การใช้งาน' },
    ],
  },
];

function Logo() {
  return (
    <svg width="24" height="24" viewBox="0 0 32 32" aria-hidden>
      <rect width="32" height="32" rx="7" fill="var(--color-brand-mark)" />
      <g fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 27v-8" />
        <path d="M16 21l-4.5-3M16 21l4.5-3" />
      </g>
      <g fill="#d6f5e3">
        <circle cx="10.5" cy="12" r="4.2" />
        <circle cx="21.5" cy="12" r="4.2" />
        <circle cx="16" cy="8.5" r="4.6" />
      </g>
    </svg>
  );
}

function Sidebar({ pendingCount }: { pendingCount: number }) {
  return (
    <nav className="flex w-56 shrink-0 flex-col gap-6 border-r border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-4">
      <div className="flex items-center gap-2 px-2">
        <Logo />
        <span className="text-[15px] font-semibold tracking-tight">Broccoli</span>
      </div>

      {NAV.map((group) => (
        <div key={group.group} className="flex flex-col gap-0.5">
          <p className="px-2 pb-1 text-[11px] font-semibold tracking-wide text-[var(--color-text-subtle)] uppercase">
            {group.group}
          </p>
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-between rounded-lg px-2 py-1.5 text-[14px]',
                  isActive
                    ? 'bg-[var(--color-surface-selected)] font-medium text-[var(--color-primary-700)]'
                    : 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-hover)]',
                )
              }
            >
              {item.label}
              {item.badge === 'approvals' && pendingCount > 0 ? (
                <span className="rounded-full bg-[var(--color-warning-surface)] px-1.5 text-[11px] font-semibold text-[var(--color-warning)]">
                  {pendingCount}
                </span>
              ) : null}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}

/**
 * The shell is also the auth boundary. `/auth/me` is the one request that decides whether a
 * person is signed in, so every screen inside is rendered only after it answers — no page
 * has to handle "maybe there is no session".
 */
export function AppShell() {
  const me = useMe();
  const location = useLocation();
  const approvals = useApprovals('pending', { enabled: me.isSuccess });
  const logout = useLogout();

  /* A queued write refused with 401 is held, not dropped (offline-queue.ts rule 3). This is
     the moment it becomes sendable again: there is a session, so try the outbox once more. */
  useEffect(() => {
    if (me.isSuccess) void offlineQueue.flush();
  }, [me.isSuccess]);

  if (me.isPending) {
    return (
      <div className="flex h-dvh items-center justify-center">
        <Skeleton className="h-8 w-40" />
      </div>
    );
  }

  if (me.isError) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return (
    <div className="flex h-dvh bg-[var(--color-surface-sunken)]">
      <Sidebar pendingCount={approvals.data?.length ?? 0} />

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center justify-end gap-3 border-b border-[var(--color-border)] bg-[var(--color-surface)] px-5">
          <span className="text-[13px] text-[var(--color-text-muted)]">
            {me.data.user.name}
            {me.data.roleKey ? ` · ${me.data.roleKey}` : ''}
          </span>
          <Avatar name={me.data.user.name} />
          <button
            type="button"
            onClick={() => {
              logout.mutate(undefined, { onSuccess: () => window.location.assign('/login') });
            }}
            className="text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
          >
            ออกจากระบบ
          </button>
        </header>

        <main className="min-h-0 flex-1 overflow-auto">
          {/* Mounted once, here: what is still only on this phone must follow the person to
              whatever screen they are on, not vanish when they leave the page that queued it. */}
          <div className="px-5 pt-5 empty:hidden">
            <OfflineBanner />
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text)]">
          {title}
        </h1>
        {subtitle ? (
          <p className="mt-0.5 text-[13px] text-[var(--color-text-muted)]">{subtitle}</p>
        ) : null}
      </div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}
